'use client'

import { ReactNode } from 'react'
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Info,
  ShieldAlert,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type AlertTone =
  | 'info'
  | 'warning'
  | 'danger'
  | 'neutral'

type Props = {
  title: string
  message: string
  tone?: AlertTone
  icon?: ReactNode

  ctaLabel?: string
  onCta?: () => void

  secondaryLabel?: string
  onSecondary?: () => void

  meta?: ReactNode

  onDismiss?: () => void

  currentIndex?: number
  totalAlerts?: number
  onPrevious?: () => void
  onNext?: () => void

  className?: string
}

function getToneClasses(tone: AlertTone) {
  switch (tone) {
    case 'danger':
      return {
        glow: 'bg-red-500/15',
        iconWrap:
          'bg-red-500/10 text-red-500 ring-red-500/15',
        indicator: 'bg-red-500',
        button:
          'bg-red-600 text-white hover:bg-red-700',
      }

    case 'warning':
      return {
        glow: 'bg-yellow-500/15',
        iconWrap:
          'bg-yellow-500/10 text-yellow-600 ring-yellow-500/15 dark:text-yellow-400',
        indicator: 'bg-yellow-500',
        button:
          'bg-yellow-500 text-black hover:bg-yellow-400',
      }

    case 'info':
      return {
        glow: 'bg-blue-500/15',
        iconWrap:
          'bg-blue-500/10 text-blue-500 ring-blue-500/15',
        indicator: 'bg-blue-500',
        button:
          'bg-foreground text-background hover:bg-foreground/90',
      }

    default:
      return {
        glow: 'bg-primary/10',
        iconWrap:
          'bg-muted text-muted-foreground ring-border',
        indicator: 'bg-muted-foreground',
        button:
          'bg-foreground text-background hover:bg-foreground/90',
      }
  }
}

function getDefaultIcon(tone: AlertTone) {
  switch (tone) {
    case 'danger':
      return <ShieldAlert className="h-[18px] w-[18px]" />

    case 'warning':
      return <AlertTriangle className="h-[18px] w-[18px]" />

    case 'info':
      return <Info className="h-[18px] w-[18px]" />

    default:
      return <CircleAlert className="h-[18px] w-[18px]" />
  }
}

export function WorkspaceAlert({
  title,
  message,
  tone = 'warning',
  icon,

  ctaLabel,
  onCta,

  secondaryLabel,
  onSecondary,

  meta,

  onDismiss,

  currentIndex = 0,
  totalAlerts = 1,
  onPrevious,
  onNext,

  className,
}: Props) {
  const styles = getToneClasses(tone)

  const hasMultiple =
    totalAlerts > 1

  return (
    <div
      className={cn(
        `
          relative
          overflow-hidden
          rounded-[22px]
          border border-border/60
          bg-background/94
          shadow-[0_24px_80px_-24px_rgba(0,0,0,0.45)]
          backdrop-blur-2xl
          supports-[backdrop-filter]:bg-background/88
        `,
        className
      )}
    >
      {/*
       * Glow
       */}
      <div
        className={cn(
          `
            pointer-events-none
            absolute
            -left-12
            -top-20
            h-40
            w-40
            rounded-full
            blur-[55px]
          `,
          styles.glow
        )}
      />

      {/*
       * Top accent
       */}
      <div
        className={cn(
          'absolute left-0 top-0 h-[2px] w-full opacity-60',
          styles.indicator
        )}
      />

      <div className="relative px-4 pb-4 pt-3.5 sm:px-5 sm:pb-5">
        {/*
         * Top controls
         */}
        <div className="mb-3 flex min-h-7 items-center justify-end gap-1">
          {hasMultiple ? (
            <div
              className="
                mr-1
                flex
                items-center
                rounded-full
                border border-border/60
                bg-muted/35
                p-0.5
              "
            >
              <button
                type="button"
                onClick={onPrevious}
                className="
                  flex h-6 w-6
                  items-center justify-center
                  rounded-full
                  text-muted-foreground
                  transition
                  hover:bg-background
                  hover:text-foreground
                "
                aria-label="Alerta anterior"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <span
                className="
                  min-w-[42px]
                  px-1
                  text-center
                  text-[10px]
                  font-semibold
                  tabular-nums
                  text-muted-foreground
                "
              >
                {currentIndex + 1} / {totalAlerts}
              </span>

              <button
                type="button"
                onClick={onNext}
                className="
                  flex h-6 w-6
                  items-center justify-center
                  rounded-full
                  text-muted-foreground
                  transition
                  hover:bg-background
                  hover:text-foreground
                "
                aria-label="Siguiente alerta"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}

          {onDismiss ? (
            <button
              type="button"
              onClick={onDismiss}
              className="
                flex h-7 w-7
                items-center justify-center
                rounded-full
                text-muted-foreground
                transition
                hover:bg-muted
                hover:text-foreground
              "
              aria-label="Minimizar alerta"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/*
         * Content
         */}
        <div className="flex items-start gap-3.5">
          <div
            className={cn(
              `
                flex
                h-10 w-10
                shrink-0
                items-center
                justify-center
                rounded-[13px]
                ring-1
              `,
              styles.iconWrap
            )}
          >
            {icon ?? getDefaultIcon(tone)}
          </div>

          <div className="min-w-0 flex-1">
            <div
              className="
                pr-1
                text-[14px]
                font-semibold
                leading-5
                tracking-[-0.01em]
                text-foreground
              "
            >
              {title}
            </div>

            <p
              className="
                mt-1
                text-[13px]
                leading-[1.55]
                text-muted-foreground
              "
            >
              {message}
            </p>

            {meta ? (
              <div
                className="
                  mt-3
                  text-[11px]
                  leading-4
                  text-muted-foreground/80
                "
              >
                {meta}
              </div>
            ) : null}
          </div>
        </div>

        {/*
         * Actions
         */}
        {(ctaLabel || secondaryLabel) ? (
          <div
            className="
              mt-4
              flex
              items-center
              justify-end
              gap-2
              border-t
              border-border/50
              pt-3
            "
          >
            {secondaryLabel && onSecondary ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onSecondary}
                className="h-8 rounded-xl px-3 text-xs"
              >
                {secondaryLabel}
              </Button>
            ) : null}

            {ctaLabel && onCta ? (
              <Button
                type="button"
                size="sm"
                onClick={onCta}
                className={cn(
                  'h-8 rounded-xl px-3.5 text-xs font-semibold',
                  styles.button
                )}
              >
                {ctaLabel}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}